import React, { useCallback, useState } from 'react'
import {
  Box,
  Button,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core'
import { Upload as UploadIcon, Download as DownloadIcon } from 'react-feather'
import { useDropzone } from 'react-dropzone'
import PropTypes from 'prop-types'

import { BaseDialog, LoadingFeedback } from 'components'
import { getBaseURL } from 'service/env'
import * as service from 'service'
import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'

const ImportParticipantsDialog = ({
  open,
  setOpen,
  phishingCampaignId,
  onImport,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles[0])
    setImportErrors([])
  }, [])

  const { getRootProps, getInputProps } = useDropzone({ onDrop })
  const { ref, ...rootProps } = getRootProps()

  const downloadTemplate = () => {
    window.open(
      `${getBaseURL('dponet')}/modelo_importacao_participantes_phishing.xlsx`,
      '_blank',
    )
  }

  const validateFile = () => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ]
    return file && validTypes.includes(file.type)
  }

  const handleSubmit = async () => {
    if (!validateFile()) {
      snackbar.open({
        message: 'Selecione um documento Excel (.xlsx)',
        variant: 'error',
      })
      return
    }

    setLoading(true)
    try {
      const response =
        await service.dponet.phishingCampaigns.importParticipants(
          phishingCampaignId,
          file,
        )

      const { participants, errors } = response?.data || {}

      if (errors?.length > 0) {
        setImportErrors(errors)
      }

      if (participants?.length > 0) {
        participants.forEach((participant) => {
          onImport({
            name: participant.name,
            email: participant.email,
            department: {
              id: participant.departmentId,
              name: participant.departmentName,
            },
          })
        })

        snackbar.open({
          message: `${participants.length} participante(s) importado(s) com sucesso`,
          variant: 'success',
        })
      }

      if (participants?.length === 0 && errors?.length === 0) {
        snackbar.open({
          message: 'Nenhum participante encontrado na planilha',
          variant: 'warning',
        })
        return
      }

      if (errors?.length === 0) {
        setOpen(false)
        setFile(null)
      }
    } catch (error) {
      snackbar.open({
        message:
          error?.response?.data?.error ||
          'Erro ao importar participantes. Verifique a planilha e tente novamente.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFile(null)
    setImportErrors([])
    setOpen(false)
  }

  return (
    <BaseDialog
      open={open}
      setOpen={handleClose}
      closeButtonText="Cancelar"
      actionButtonText="Importar"
      title="Importar participantes externos"
      actionButton={handleSubmit}
    >
      <LoadingFeedback open={loading} />
      <Box className={classes.box}>
        <Typography align="justify" className={classes.typography}>
          Importe participantes externos via planilha Excel. As colunas devem
          ser: <strong>Nome</strong>, <strong>E-mail</strong> e{' '}
          <strong>Departamento</strong>. O departamento deve corresponder
          exatamente a um departamento cadastrado na empresa.
        </Typography>

        <Link onClick={downloadTemplate}>
          <Button
            variant="contained"
            color="primary"
            className={classes.buttonDownloadModel}
          >
            <DownloadIcon />
            <Typography>Baixar modelo</Typography>
          </Button>
        </Link>

        <Box {...rootProps} className={classes.dropzone} rootref={ref} mt={2}>
          <input {...getInputProps()} />
          <Box m={1} mt={2}>
            <UploadIcon />
          </Box>
          <Box m={1} mb={2}>
            <Typography>
              Arraste e solte ou clique para selecionar a planilha com os
              participantes
            </Typography>
          </Box>
        </Box>

        {file && (
          <Box mt={1}>
            <Typography variant="body2">
              Arquivo: <strong>{file.name}</strong>
            </Typography>
          </Box>
        )}

        {importErrors.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="error">
              Linhas ignoradas:
            </Typography>
            <List dense disablePadding>
              {importErrors.map((importError) => (
                <ListItem
                  key={`${importError.line}-${importError.email}`}
                  disableGutters
                >
                  <ListItemText
                    primary={`Linha ${importError.line} — ${importError.name} (${importError.email}): ${importError.message}`}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'error',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </BaseDialog>
  )
}

ImportParticipantsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  phishingCampaignId: PropTypes.number.isRequired,
  onImport: PropTypes.func.isRequired,
}

export default ImportParticipantsDialog
